import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../Servicios/supabaseClient.js';
import { useAuth } from '../../Context/AuthContext';
import titulo from '../../Img/titulo.png';
import { Icon } from '../../Components/ui/ui.jsx';
import { initials } from './format.js';
import './Dashboard.css';

const MAIN_NAV = [
    { to: '/dashboard', end: true, icon: 'grid', label: 'Resumen' },
    { to: '/dashboard/facturas', icon: 'file', label: 'Facturas' },
    { to: '/dashboard/exportar', icon: 'download', label: 'Exportar ventas' },
];

const ACCOUNT_NAV = [
    { to: '/dashboard/arca', icon: 'shield', label: 'Conexión ARCA', arcaDot: true },
    { to: '/dashboard/team', icon: 'users', label: 'Team' },
    { to: '/dashboard/cuentas', icon: 'link', label: 'Cuentas linkeadas' },
];

const ARCA_DOT = {
    ok: { color: 'var(--primary)', label: 'Conectado' },
    pendiente_validacion: { color: 'var(--warn)', label: 'Validando' },
    error: { color: 'var(--danger)', label: 'Con error' },
    unconfigured: { color: 'var(--danger)', label: 'Sin configurar' },
};

// Layout del dashboard: barra lateral + la página activa.
// Carga una vez los datos que comparten todas las páginas (perfil, plan, estado de ARCA)
// y los pasa por el contexto del <Outlet>.
export default function DashboardLayout() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [profile, setProfile] = useState(null);
    const [subscription, setSubscription] = useState(null);
    const [plan, setPlan] = useState(null);
    const [afipStatus, setAfipStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [toast, setToast] = useState(null);
    const toastTimer = useRef(null);

    useEffect(() => {
        if (!user) return;
        let cancelled = false;

        async function load() {
            setLoading(true);
            const [profileRes, afipRes] = await Promise.all([
                supabase.from('profiles').select('*, subscriptions(*, plans(*))').eq('id', user.id).single(),
                supabase.from('afip_credentials').select('status').eq('user_id', user.id).maybeSingle(),
            ]);
            if (cancelled) return;

            if (profileRes.error) {
                setError(profileRes.error.message);
            } else if (profileRes.data) {
                setProfile(profileRes.data);
                setSubscription(profileRes.data.subscriptions || null);
                setPlan(profileRes.data.subscriptions?.plans || null);
            }
            setAfipStatus(afipRes.data?.status || 'unconfigured');
            setLoading(false);
        }

        load();
        return () => { cancelled = true; };
    }, [user]);

    // Cerrar el menú mobile al navegar
    useEffect(() => { setMenuOpen(false); }, [location.pathname]);

    useEffect(() => () => clearTimeout(toastTimer.current), []);

    const notify = useCallback((message, tone = 'ok') => {
        clearTimeout(toastTimer.current);
        setToast({ message, tone });
        toastTimer.current = setTimeout(() => setToast(null), 4500);
    }, []);

    const handleLogout = async () => {
        const { error: signOutError } = await supabase.auth.signOut();
        if (signOutError) {
            notify('No pudimos cerrar la sesión. Probá de nuevo.', 'danger');
            return;
        }
        navigate('/login');
    };

    if (loading) {
        return (
            <div className="dashLoading" role="status">
                <img src={titulo} alt="QuantumNodo" />
                <span>Cargando tu panel…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashLoading" role="alert">
                <img src={titulo} alt="QuantumNodo" />
                <span>No pudimos cargar tu panel: {error}</span>
            </div>
        );
    }

    const name = profile?.nombre || user.email;
    const dot = ARCA_DOT[afipStatus] || ARCA_DOT.unconfigured;

    const renderLink = (item) => (
        <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `navLink${isActive ? ' isActive' : ''}`}
        >
            <Icon name={item.icon} className="navIcon" />
            <span className="navLabel">{item.label}</span>
            {item.arcaDot && (
                <span className="navDot" style={{ background: dot.color }} title={dot.label}>
                    <span className="srOnly">{dot.label}</span>
                </span>
            )}
        </NavLink>
    );

    return (
        <div className="dashShell">
            <header className="dashTopbar">
                <img src={titulo} alt="QuantumNodo" className="dashTopbarLogo" />
                <button
                    type="button"
                    className="iconBtn"
                    aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
                    aria-expanded={menuOpen}
                    aria-controls="dash-sidebar"
                    onClick={() => setMenuOpen((o) => !o)}
                >
                    <Icon name={menuOpen ? 'close' : 'menu'} size={20} />
                </button>
            </header>

            <aside id="dash-sidebar" className={`dashSidebar${menuOpen ? ' isOpen' : ''}`}>
                <img src={titulo} alt="QuantumNodo" className="dashSidebarLogo" />
                <nav aria-label="Principal" className="navGroup">
                    {MAIN_NAV.map(renderLink)}
                </nav>
                <div className="navGroup">
                    <span className="overline navHeading">Cuenta</span>
                    {ACCOUNT_NAV.map(renderLink)}
                </div>
                <div className="sidebarUser">
                    <div className="sidebarUserRow">
                        <span className="avatar" aria-hidden="true">{initials(profile?.nombre || user.email)}</span>
                        <div className="sidebarUserText">
                            <span className="sidebarUserName">{name}</span>
                            {profile?.empresa && <span className="sidebarUserCompany">{profile.empresa}</span>}
                        </div>
                    </div>
                    <button type="button" className="logoutBtn" onClick={handleLogout}>Cerrar sesión</button>
                </div>
            </aside>

            {menuOpen && <div className="dashScrim" onClick={() => setMenuOpen(false)} aria-hidden="true" />}

            <main className="dashMain">
                <Outlet context={{ user, profile, plan, subscription, afipStatus, setAfipStatus, notify }} />
            </main>

            {toast && (
                <div className={`toast toast-${toast.tone}`} role="status">
                    <Icon name={toast.tone === 'ok' ? 'check' : 'alert'} size={18} />
                    <span>{toast.message}</span>
                    <button type="button" className="iconBtn iconBtnSm" aria-label="Cerrar aviso" onClick={() => setToast(null)}>
                        <Icon name="close" size={16} />
                    </button>
                </div>
            )}
        </div>
    );
}
